/**
 * Orders: everything open on this site, in one list.
 *
 * There used to be three places an order could be: a tab on the Tabs screen, a
 * table on the Room screen, and a paid-for lunch on the Counter screen. A new
 * starter had to know which kind of order it was before they could find it.
 * Now every open order is here, named the way it is said out loud — "Table 4",
 * "Counter 12", "Tab 6" — and the counter's paid-and-waiting orders sit at the
 * top, because those are people standing at the till.
 *
 * One "New order" button starts all three kinds. It only asks which kind when
 * there is more than one this restaurant can take.
 */

import { useState } from 'react';
import {
  formatMoney,
  useEntitlements,
  useFloor,
  useOpenOrder,
  useOpenOrders,
} from '@restaurant/api-client';
import { ActionError, Button, Empty, ErrorNotice, Loading, Sheet } from '@restaurant/ui';

import { WaitingToCollect } from './counter';
import { FoodUpTag } from './food-up';
import { orderName, useTableLabels } from './order-name';
import { useFoodUp } from './readiness';

export function Orders({
  locationId,
  onOpen,
  onPickTable,
}: {
  locationId: string | undefined;
  onOpen: (orderId: string) => void;
  /** Starting an order at a table happens on the tables screen. */
  onPickTable: () => void;
}) {
  const orders = useOpenOrders(locationId);
  const floor = useFloor(locationId);
  const entitlements = useEntitlements();
  const openOrder = useOpenOrder();
  const foodUp = useFoodUp((orders.data ?? []).map((order) => order.id));
  const [choosing, setChoosing] = useState(false);

  const counter = entitlements.has('orders.qsr_mode');
  const hasTables = (floor.data ?? []).some((plan) => plan.tables.length > 0);
  const tableLabel = useTableLabels(locationId);

  function start(kind: 'tab' | 'counter') {
    if (!locationId) return;
    setChoosing(false);
    openOrder.mutate(
      // A counter order is one person's lunch: one cover, no table.
      kind === 'counter'
        ? { location_id: locationId, guest_count: 1, service_mode: 'counter' }
        : { location_id: locationId, guest_count: 2 },
      { onSuccess: (order) => onOpen(order.id) },
    );
  }

  function newOrder() {
    // Only ask when there is a choice to make.
    if (!hasTables && !counter) start('tab');
    else setChoosing(true);
  }

  const open = orders.data ?? [];

  return (
    <div className="pos__view">
      <div className="pos__view-head">
        <h1 className="pos__view-title">
          Orders
          {open.length > 0 && <span className="pos__view-count">{open.length}</span>}
        </h1>
        <Button variant="primary" size="lg" onClick={newOrder} loading={openOrder.isPending}>
          New order
        </Button>
      </div>

      <div className="pos__view-body">
        <ActionError error={openOrder.error} action="Starting an order" />

        {counter && <WaitingToCollect locationId={locationId} onOpenOrder={onOpen} />}

        {orders.isPending && <Loading label="Loading orders" />}
        {orders.isError && <ErrorNotice message={orders.error.message} />}

        {orders.data &&
          (open.length === 0 ? (
            <Empty
              title="No open orders"
              description="Tap New order to start one. It stays here until it is paid."
            />
          ) : (
            <>
              <h2 className="ord__heading">Open</h2>
              <div className="tabs">
                {open.map((order) => {
                  const items = order.lines.filter((l) => l.state !== 'voided').length;
                  return (
                    <button
                      key={order.id}
                      type="button"
                      className="tab"
                      onClick={() => onOpen(order.id)}
                    >
                      <span className="tab__number">{orderName(order, tableLabel)}</span>
                      <FoodUpTag food={foodUp(order.id)} />
                      <span className="tab__meta">
                        {order.service_mode === 'counter'
                          ? ''
                          : `${order.guest_count} guest${order.guest_count === 1 ? '' : 's'} · `}
                        {items === 0 ? 'nothing yet' : `${items} item${items === 1 ? '' : 's'}`}
                      </span>
                      <span className="tab__total tabular">{formatMoney(order.total)}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ))}
      </div>

      {choosing && (
        <Sheet title="New order" onDismiss={() => setChoosing(false)}>
          <ul className="os__more">
            {hasTables && (
              <li>
                <button
                  type="button"
                  className="os__more-item"
                  onClick={() => {
                    setChoosing(false);
                    onPickTable();
                  }}
                >
                  <span className="os__more-label">At a table</span>
                  <span className="os__more-hint">Pick the table they are sitting at.</span>
                </button>
              </li>
            )}
            {counter && (
              <li>
                <button
                  type="button"
                  className="os__more-item"
                  onClick={() => start('counter')}
                >
                  <span className="os__more-label">Counter / takeaway</span>
                  <span className="os__more-hint">
                    They pay now and get a number to collect with.
                  </span>
                </button>
              </li>
            )}
            <li>
              <button type="button" className="os__more-item" onClick={() => start('tab')}>
                <span className="os__more-label">Tab with no table</span>
                <span className="os__more-hint">At the bar, or anywhere without a table.</span>
              </button>
            </li>
          </ul>
        </Sheet>
      )}
    </div>
  );
}

/**
 * One tap to a counter sale, from whichever screen the till is on.
 *
 * A café's till is mostly the counter: somebody orders a coffee, pays, and
 * waits for their number. Reaching that took Orders, New order, then
 * "Counter / takeaway" before the first drink could go on, three taps a sale
 * through a rush. This sits in the till's top bar instead, on every plan that
 * has counter service, and opens the new order straight away. New order still
 * offers a tab or a table.
 */
export function CounterSaleButton({
  locationId,
  onOpen,
}: {
  locationId: string | undefined;
  onOpen: (orderId: string) => void;
}) {
  const entitlements = useEntitlements();
  const openOrder = useOpenOrder();
  if (!entitlements.has('orders.qsr_mode')) return null;

  return (
    <>
      <Button
        variant="primary"
        disabled={!locationId}
        loading={openOrder.isPending}
        onClick={() =>
          locationId &&
          openOrder.mutate(
            // A counter order is one person's order: one cover, no table.
            { location_id: locationId, guest_count: 1, service_mode: 'counter' },
            { onSuccess: (order) => onOpen(order.id) },
          )
        }
      >
        Counter sale
      </Button>
      {openOrder.error && (
        <div className="pos__bar-error">
          <ActionError error={openOrder.error} action="Starting a counter sale" />
        </div>
      )}
    </>
  );
}
